/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  GamoraBase64URLGuard,
  GamoraErrorUnhandledException,
  assert,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakServiceSessionGuardTags,
  HoruzProtocolChulakServiceSessionGuardTagsType,
} from '../service-session.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakMessageGuard } from './base.js';

/**
 * Input type
 * @public
 */
export type HoruzProtocolChulakServiceSessionGuardUpdatedTagsType = Exclude<
  HoruzProtocolChulakServiceSessionGuardTags,
  { type: HoruzProtocolChulakServiceSessionGuardTagsType.Dynamic }
>;

/**
 * Input type
 * @public
 */
export interface HoruzProtocolChulakMessageAppTagsRefreshedGuardInput {
  tags: HoruzProtocolChulakServiceSessionGuardUpdatedTagsType;
}

/**
 * @public
 */
export class HoruzProtocolChulakMessageAppTagsRefreshedGuard extends HoruzProtocolChulakMessageGuard<
  HoruzProtocolChulakDataType.DATA_APP_TAGS_REFRESHED,
  HoruzProtocolChulakMessageAppTagsRefreshedGuardInput
> {
  private _tags: HoruzProtocolChulakServiceSessionGuardUpdatedTagsType;
  /**
   * Constructor
   * @param input - the input
   */
  constructor(input: HoruzProtocolChulakMessageAppTagsRefreshedGuardInput) {
    super();

    this._tags = input.tags;
  }

  /**
   * Gets type
   */
  get type(): HoruzProtocolChulakDataType.DATA_APP_TAGS_REFRESHED {
    return HoruzProtocolChulakDataType.DATA_APP_TAGS_REFRESHED;
  }

  /**
   * Tags
   */
  get tags() {
    return structuredClone(this._tags);
  }
  set tags(tags: HoruzProtocolChulakServiceSessionGuardUpdatedTagsType) {
    this._tags = structuredClone(tags);
  }

  /**
   * Gets from JSON
   * @param input - Input string
   */
  static override fromJSON(
    input: unknown,
  ): HoruzProtocolChulakMessageAppTagsRefreshedGuard {
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not an object.',
    );
    return new HoruzProtocolChulakMessageAppTagsRefreshedGuard(
      input as HoruzProtocolChulakMessageAppTagsRefreshedGuardInput,
    );
  }

  /**
   * Get from base64URL
   * @param input - The input
   */
  static override fromBase64URL(
    input: unknown,
  ): HoruzProtocolChulakMessageAppTagsRefreshedGuard {
    assert(
      typeof input === 'string' && input.length > 0,
      'Invalid input argument: not a valid string.',
    );

    // Parse input
    let json;
    try {
      json = JSON.parse(GamoraBase64URLGuard.fromBase64URL(input).toUTF8());
    } catch (e) {
      throw new GamoraErrorUnhandledException(
        'Unable to parse input: not valid JSON in base64url input.',
      );
    }

    // Create new guard
    return new HoruzProtocolChulakMessageAppTagsRefreshedGuard(
      json as HoruzProtocolChulakMessageAppTagsRefreshedGuardInput,
    );
  }

  /**
   * Gets JSON
   * @returns string
   */
  override toJSON() {
    return {
      tags: this._tags,
    };
  }

  /**
   * To Base64URL
   */
  override toBase64URL(): string {
    const json = this.toJSON();
    return GamoraBase64URLGuard.fromUTF8(JSON.stringify(json)).toBase64URL();
  }

  /**
   * Create clone
   */
  clone() {
    return HoruzProtocolChulakMessageAppTagsRefreshedGuard.fromJSON(
      this.toJSON(),
    );
  }
}
